import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, CalendarDays, CreditCard, LogOut, UsersRound, Building2, Trophy, Activity, Trash2, Loader2, Image as ImageIcon, Settings, LayoutDashboard, BriefcaseBusiness, BarChart3, Cable, Landmark, ContactRound, HandCoins, Search } from "lucide-react";
import { toast } from "sonner";
import { AdminDataTable } from "../components/fyllr/AdminDataTable";
import { AdminContentPanel } from "../components/fyllr/AdminContentPanel";
import { MetaSettingsCard } from "../components/fyllr/MetaSettingsCard";
import { AdminOverview } from "../components/fyllr/AdminOverview";
import { PartnerCRM } from "../components/fyllr/PartnerCRM";
import { AdminSessionsPanel } from "../components/fyllr/AdminSessionsPanel";
import { AdminSubscriptions } from "../components/fyllr/AdminSubscriptions";
import { AdminReporting } from "../components/fyllr/AdminReporting";
import { AdminIntegrations } from "../components/fyllr/AdminIntegrations";
import { AdminClientsPanel } from "../components/fyllr/AdminClientsPanel";
import { AdminReferrals } from "../components/fyllr/AdminReferrals";
import { AdminSeoEngine } from "../components/fyllr/AdminSeoEngine";
import { getAdminDashboardData, getAdminInsights, deleteAdminRecord, clearAdminTestData, getMe, salonLogout } from "../lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";

const tabs = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "partner-crm", label: "Partner CRM", icon: BriefcaseBusiness },
  { id: "clients", label: "Clients", icon: ContactRound },
  { id: "sessions", label: "Sessions", icon: CalendarDays },
  { id: "users", label: "Users", icon: UsersRound },
  { id: "partners", label: "Partners", icon: Building2 },
  { id: "bookings", label: "Bookings", icon: CreditCard },
];
const dataTabs = tabs.filter((tab) => ["users", "partners", "bookings", "sessions"].includes(tab.id));
const extraTabs = [
  { id: "subscriptions", label: "Subscriptions", icon: Landmark },
  { id: "reporting", label: "Reporting", icon: BarChart3 },
  { id: "integrations", label: "Integrations", icon: Cable },
  { id: "referrals", label: "Referrals", icon: HandCoins },
  { id: "seo", label: "SEO", icon: Search },
  { id: "content", label: "Content", icon: ImageIcon },
  { id: "settings", label: "Settings", icon: Settings },
];
const allTabs = [...tabs, ...extraTabs];
const columns = {
  users: [{ key: "email", label: "Email" }, { key: "role", label: "Type" }, { key: "preferred_categories", label: "Categories" }, { key: "preferred_times", label: "Times" }, { key: "created_at", label: "Joined" }],
  partners: [{ key: "salon_name", label: "Venue" }, { key: "contact_email", label: "Contact" }, { key: "suburb", label: "Suburb" }, { key: "status", label: "Status" }, { key: "created_at", label: "Registered" }],
  bookings: [{ key: "customer_email", label: "Customer" }, { key: "service", label: "Booking" }, { key: "salon_name", label: "Venue" }, { key: "amount", label: "Amount", format: (value) => `$${Number(value || 0).toFixed(2)}` }, { key: "status", label: "Status" }, { key: "created_at", label: "Created" }],
  sessions: [{ key: "service_name", label: "Session" }, { key: "salon_name", label: "Venue" }, { key: "category", label: "Category" }, { key: "date_str", label: "Date" }, { key: "time", label: "Time" }, { key: "spots_left", label: "Spots left" }],
};

function InsightCard({ label, value, testid }) {
  return (
    <div className="border border-sand/70 bg-bone rounded-md px-5 py-4" data-testid={testid}>
      <p className="text-[10px] tracking-[0.22em] uppercase text-mocha">{label}</p>
      <p className="mt-2 font-serif text-3xl md:text-4xl text-ink leading-none">{value}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [records, setRecords] = useState({ users: [], partners: [], bookings: [], sessions: [] });
  const [insights, setInsights] = useState(null);
  const [status, setStatus] = useState("loading");
  const [admin, setAdmin] = useState(null);
  const [clearing, setClearing] = useState(false);
  const [selectedPartnerId, setSelectedPartnerId] = useState(null);

  const loadAll = async () => {
    const [values, insightsData] = await Promise.all([
      Promise.all(dataTabs.map((tab) => getAdminDashboardData(tab.id))),
      getAdminInsights(),
    ]);
    setRecords(Object.fromEntries(dataTabs.map((tab, index) => [tab.id, values[index]])));
    setInsights(insightsData);
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const me = await getMe();
        if (me.role !== "admin") { if (mounted) setStatus("forbidden"); return; }
        if (mounted) setAdmin(me);
        await loadAll();
        if (mounted) setStatus("ready");
      } catch (error) {
        if (!mounted) return;
        setStatus(error?.response?.status === 401 ? "unauthenticated" : "forbidden");
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (status === "unauthenticated" || status === "forbidden") navigate("/admin/login", { replace: true });
  }, [status, navigate]);

  const active = useMemo(() => allTabs.find((tab) => tab.id === activeTab), [activeTab]);

  const logout = async () => {
    try { await salonLogout(); } finally { toast.success("Signed out."); navigate("/admin/login", { replace: true }); }
  };

  const handleDelete = async (recordId) => {
    try {
      await deleteAdminRecord(activeTab, recordId);
      setRecords((prev) => ({ ...prev, [activeTab]: prev[activeTab].filter((row) => row.id !== recordId) }));
      toast.success("Record deleted.");
      try { setInsights(await getAdminInsights()); } catch { /* non-blocking */ }
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not delete record.");
    }
  };

  const handleClearTestData = async () => {
    setClearing(true);
    try {
      const result = await clearAdminTestData();
      const cleared = result?.cleared || {};
      toast.success(`Cleared ${cleared.users || 0} users and ${(cleared.bookings || 0) + (cleared.session_bookings || 0)} bookings.`);
      await loadAll();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not clear data.");
    } finally {
      setClearing(false);
    }
  };

  if (status === "loading" || status === "unauthenticated" || status === "forbidden") return <div className="min-h-screen bg-bone" data-testid="admin-dashboard-loading" />;

  return (
    <div className="min-h-screen bg-[#F5EFE6] pt-14 md:pt-16 pb-16 text-[#3D0A1E]" data-testid="admin-dashboard-page">
      <div className="max-w-[1320px] mx-auto px-5 md:px-8 py-8 md:py-11">
        <div className="flex items-start justify-between gap-5 flex-wrap reveal">
          <div>
            <Link to="/" className="inline-flex items-center gap-2 text-xs text-ink/55 hover:text-espresso transition-colors" data-testid="admin-dashboard-back"><ArrowLeft size={14} /> FYLLR home</Link>
            <p className="mt-5 text-[10px] tracking-[0.25em] uppercase text-mocha" data-testid="admin-dashboard-eyebrow">Marketplace command center</p>
            <h1 className="mt-2 font-serif text-4xl md:text-5xl text-ink" data-testid="admin-dashboard-heading">Command center.</h1>
            <p className="mt-2 text-sm text-ink/55" data-testid="admin-dashboard-identity">Signed in as {admin?.email}</p>
          </div>
          <button type="button" onClick={logout} className="inline-flex items-center gap-2 border border-sand rounded-full px-4 py-2 text-xs text-ink/70 hover:border-espresso hover:text-espresso transition-colors" data-testid="admin-dashboard-logout"><LogOut size={13} /> Log out</button>
        </div>

        {/* ---- Insights bar ---- */}
        {insights && activeTab !== "overview" && activeTab !== "partner-crm" && (
          <div className="mt-10 reveal" data-testid="admin-insights-bar">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <InsightCard label="Waitlist users" value={insights.total_users} testid="admin-insight-users" />
              <InsightCard label="Partner venues" value={insights.total_partners} testid="admin-insight-partners" />
              <InsightCard label="Total bookings" value={insights.total_bookings} testid="admin-insight-bookings" />
            </div>
            <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
              <div className="border border-sand/70 bg-bone rounded-md px-5 py-4" data-testid="admin-insight-top-venues">
                <p className="flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-mocha"><Trophy size={13} /> Top venues</p>
                {insights.top_venues.length === 0 ? (
                  <p className="mt-3 text-sm text-ink/45" data-testid="admin-top-venues-empty">No bookings yet.</p>
                ) : (
                  <ol className="mt-3 space-y-2">
                    {insights.top_venues.map((venue, i) => (
                      <li key={venue.salon_name} className="flex items-center justify-between text-sm text-ink/80" data-testid={`admin-top-venue-${i}`}>
                        <span className="truncate"><span className="text-mocha mr-2">{i + 1}.</span>{venue.salon_name}</span>
                        <span className="text-ink/55 tabular-nums">{venue.bookings} booking{venue.bookings === 1 ? "" : "s"}</span>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
              <div className="border border-sand/70 bg-bone rounded-md px-5 py-4" data-testid="admin-insight-sync">
                <p className="flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-mocha"><Activity size={13} /> Integration sync status</p>
                <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2">
                  {insights.integrations.map((it) => (
                    <div key={it.name} className="flex items-center gap-2 text-sm text-ink/80" data-testid={`admin-sync-${it.name.toLowerCase().replace(/\s+/g, "-")}`}>
                      <span className={`inline-block h-2 w-2 rounded-full ${it.connected ? "bg-burgundy" : "bg-sand"}`} />
                      <span className="truncate">{it.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="mt-10 flex items-end justify-between gap-4 flex-wrap">
          <div className="w-full md:hidden"><label className="text-sm font-semibold text-[#3D0A1E]" data-testid="admin-tab-select-label">Dashboard section</label><select value={activeTab} onChange={(event) => setActiveTab(event.target.value)} className="mt-2 w-full border border-[#3D0A1E]/20 bg-[#F5EFE6] px-4 py-3 text-base font-semibold text-[#3D0A1E]" data-testid="admin-tab-select">{allTabs.map((tab) => <option key={tab.id} value={tab.id}>{tab.label}</option>)}</select></div>
          <div className="hidden min-w-0 flex-1 border-b border-[#3D0A1E]/15 md:block" role="tablist" aria-label="Command center sections" data-testid="admin-dashboard-tabs"><div className="flex flex-wrap gap-1">{allTabs.map((tab) => { const Icon = tab.icon; const selected = activeTab === tab.id; return <button key={tab.id} type="button" role="tab" aria-selected={selected} onClick={() => setActiveTab(tab.id)} className={`inline-flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-semibold transition-colors ${selected ? "border-[#3D0A1E] text-[#3D0A1E]" : "border-transparent text-[#3D0A1E]/60 hover:text-[#3D0A1E]"}`} data-testid={`admin-tab-${tab.id}`}><Icon size={15} /> {tab.label}</button>; })}</div></div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button type="button" className="inline-flex items-center gap-2 rounded-full border border-burgundy/40 px-4 py-2 text-xs text-burgundy hover:bg-burgundy hover:text-bone transition-colors" data-testid="admin-clear-test-data">
                <Trash2 size={13} strokeWidth={1.5} /> Clear test data
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="admin-clear-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Clear all test data?</AlertDialogTitle>
                <AlertDialogDescription>
                  This permanently deletes every waitlist user, opening booking and session booking. Partner venues, live sessions and admin accounts are kept. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="admin-clear-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleClearTestData} disabled={clearing} className="bg-burgundy hover:bg-espresso" data-testid="admin-clear-confirm">
                  {clearing ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null} Yes, clear everything
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

        <div className="mt-8 reveal" style={{ animationDelay: "120ms" }} data-testid={`admin-active-tab-${activeTab}`}>
          {activeTab === "overview" ? (
            <AdminOverview onOpenPartner={(partnerId) => { setSelectedPartnerId(partnerId); setActiveTab("partner-crm"); }} />
          ) : activeTab === "partner-crm" ? (
            <PartnerCRM selectedPartnerId={selectedPartnerId} onSelectedPartnerConsumed={() => setSelectedPartnerId(null)} />
          ) : activeTab === "clients" ? (
            <AdminClientsPanel />
          ) : activeTab === "sessions" ? (
            <AdminSessionsPanel />
          ) : activeTab === "subscriptions" ? (
            <AdminSubscriptions />
          ) : activeTab === "reporting" ? (
            <AdminReporting onOpenPartner={(partnerId) => { setSelectedPartnerId(partnerId); setActiveTab("partner-crm"); }} />
          ) : activeTab === "integrations" ? (
            <AdminIntegrations onOpenSettings={() => setActiveTab("settings")} />
          ) : activeTab === "referrals" ? (
            <AdminReferrals />
          ) : activeTab === "seo" ? (
            <AdminSeoEngine />
          ) : activeTab === "content" ? (
            <>
              <div className="mb-6"><p className="text-[10px] tracking-[0.22em] uppercase text-mocha">Content</p><h2 className="mt-1 font-serif text-3xl text-ink">Salon content for socials</h2></div>
              <AdminContentPanel />
            </>
          ) : activeTab === "settings" ? (
            <>
              <div className="mb-6"><p className="text-[10px] tracking-[0.22em] uppercase text-mocha">Settings</p><h2 className="mt-1 font-serif text-3xl text-ink">Integrations</h2></div>
              <div className="max-w-xl"><MetaSettingsCard /></div>
            </>
          ) : (
            <>
              <div className="mb-6"><p className="text-[10px] tracking-[0.22em] uppercase text-mocha">{active.label}</p><h2 className="mt-1 font-serif text-3xl text-ink">{active.label === "Sessions" ? "Live marketplace sessions" : `All ${active.label.toLowerCase()}`}</h2></div>
              <AdminDataTable section={activeTab} rows={records[activeTab]} columns={columns[activeTab]} loading={false} onDelete={handleDelete} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
